import {ModalState} from "../../../types/modals";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {Image, Video} from "../../../types/admin";
import {changeQuestionModal} from "./questionEditModal";

export type ElementModalState = ModalState & {
    requestIds: Record<string, string>
    element_id: string | null
    columnId: string
    rowId: string
    type: string
    text: string
    default_image: Image | null
    language_image: Image | null
    default_video: Video | null
    language_video: Video | null
    video_autoplay: boolean
    video_loop: boolean
    isSaving: boolean
}

const initialState: ElementModalState = {
    requestIds: {},
    loading: true,
    isOpen: false,
    error: null,
    element_id: null,
    columnId: '',
    rowId: '',
    type: '',
    text: '',
    default_image: null,
    language_image: null,
    default_video: null,
    language_video: null,
    video_autoplay: false,
    video_loop: false,
    isSaving: false
}

export const saveElement = createAsyncThunk(
    'elementModal/saveElement',
    async (payload: FormData, thunkAPI) => {
        const element_id = payload.get('element_id');
        const content_id = payload.get('content_id');
        const parentSessionId = (thunkAPI.getState() as { questionEditModal: { sessionId: string } }).questionEditModal.sessionId;
        const updateRows = (response: any) => {
            const state = thunkAPI.getState() as {
                questionEditModal: { sessionId: string },
                elementModal: ElementModalState,
            };
            if (state.questionEditModal.sessionId === parentSessionId &&
                state.elementModal.requestIds.saveElement === thunkAPI.requestId) {
                thunkAPI.dispatch(changeQuestionModal({ slide_rows: response.slide_rows }));
            }
        };
        try {
            if (element_id) {
                const response = await api.patch(`/API/admin/slides/${content_id}/elements/${element_id}/`, payload, true);
                updateRows(response);
                return response;
            } else {
                const response = await api.post(`/API/admin/slides/${content_id}/elements/`, payload, true);
                updateRows(response);
                return response;
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type getElementPayload = {
    element_id: string
    content_id: string
    language: string
}

export const getElement = createAsyncThunk(
    'elementModal/getElement',
    async (payload: getElementPayload, thunkAPI) => {
        const {element_id, content_id} = payload;
        try {
            const response = await api.get(`/API/admin/slides/${content_id}/elements/${element_id}/`, payload);
            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


type ModalPayload = {
    element_id: string | null
    type: string
    rowId: string
    columnId: string
}

const slice = createSlice({
    name: 'elementModal',
    initialState,
    reducers: {
        closeModal: () => {
            return initialState;
        },
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            return { ...initialState, ...action.payload, isOpen: true, loading: !!action.payload.element_id };
        },
        changeModal(state, action) {
            return {
                ...state,
                ...action.payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(saveElement.pending, (state, action) => {
            if (!state.isOpen) return;
            state.requestIds.saveElement = action.meta.requestId;
            state.isSaving = true;
            state.error = null;
        });
        builder.addCase(saveElement.fulfilled, (state, action) => {
            if (!state.isOpen || state.requestIds.saveElement !== action.meta.requestId) return;
            return initialState;
        });
        builder.addCase(saveElement.rejected, (state, action) => {
            if (!state.isOpen || state.requestIds.saveElement !== action.meta.requestId) return;
            state.loading = false;
            state.isSaving = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(getElement.pending, (state, action) => {
            if (!state.isOpen) return;
            state.requestIds.getElement = action.meta.requestId;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getElement.fulfilled, (state, action) => {
            if (!state.isOpen || state.requestIds.getElement !== action.meta.requestId) return;
            state.loading = false;
            state.error = null;
            Object.assign(state, action.payload);
        });
        builder.addCase(getElement.rejected, (state, action) => {
            if (!state.isOpen || state.requestIds.getElement !== action.meta.requestId) return;
            state.loading = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
    }
})

export const {openModal, closeModal, changeModal} = slice.actions;
export default slice.reducer;
